import {
  ClearValueButton,
  PasteFromClipboardButton,
} from '@drodil/backstage-plugin-toolbox';
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  InsightsIssueAction,
  InsightsIssueActionProps,
  buildInsightsIssueActionProps,
} from '@internal/yardstick-react';
import { YardstickAutoFix } from '@internal/yardstick-common';

type IssueActionPreview = Omit<InsightsIssueActionProps, 'open' | 'onClose'>;
type IssueRowInput = Parameters<typeof buildInsightsIssueActionProps>[0];

type YardstickIssuePayload = {
  id: string;
  title: string;
  message: string;
  severity: string;
  name?: string;
  dueDate?: { text: string; date: string | Date | null };
  lastUpdated?: string | Date;
  focus?: string;
  autoFix?: YardstickAutoFix;
  _meta?: {
    remediationSteps?: string;
    issueDetails?: string;
    exceptionInfo?: {
      name: string;
      href: string;
      description: string;
    };
    owningTeam?: string;
    entityInfo?: IssueRowInput['entityInfo'];
    resources?: IssueRowInput['resources'];
    frameworkId?: string;
    accountId?: string;
    subtitle?: string;
    resourceID?: string;
    resourceName?: string;
    aiGenerated?: boolean;
    [key: string]: unknown;
  };
};

const sampleIssuePayload: YardstickIssuePayload = {
  id: 'invalid-owner',
  name: 'Spec owner validation',
  title: 'Invalid spec owner',
  message: 'Add a valid spec owner',
  severity: 'high',
  dueDate: {
    date: '2025-02-25T00:00:00.000Z',
    text: 'In 3 days',
  },
  lastUpdated: '2025-01-01T00:00:00.000Z',
  _meta: {
    remediationSteps: '[Fix this issue template](#)',
    exceptionInfo: {
      name: 'Request an Exception for spec owner validation',
      href: 'https://portal.developer.principal.com/generate/templates/exception-template',
      description:
        'If you have a use case for non-standard spec owner configuration, please request an exception.',
    },
  },
  autoFix: {
    config: [
      {
        type: 'autofix',
        params: {
          body: '## Problem Statement\n\nThis is a big problem!!',
          repo: 'Your repository',
          owner: 'principalsbx-emu',
          title: '[Security] Sensitive Information Exposure',
        },
        handler: 'yardstick:github-issue',
      },
    ],
    strategy: 'sequential',
  },
};

const sampleIssueJson = JSON.stringify(sampleIssuePayload, null, 2);

const PROD_DEFAULT_ENTITY_REF =
  'component:principalinformationservices-emu/principal-developer-portal';
const LOCAL_DEFAULT_ENTITY_REF =
  'component:principalsbx-emu/principal-developer-portal';

const resolveDefaultEntityRef = (): string => {
  if (typeof window === 'undefined') {
    return PROD_DEFAULT_ENTITY_REF;
  }

  const hostname = window.location.hostname.toLowerCase();

  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname.endsWith('.local')
  ) {
    return LOCAL_DEFAULT_ENTITY_REF;
  }

  return PROD_DEFAULT_ENTITY_REF;
};

const buildEntityInfoFromEntity = (
  entity: Entity,
): IssueRowInput['entityInfo'] => {
  const entityNamespace = entity.metadata.namespace ?? 'default';

  return {
    entityName: entity.metadata.name,
    entityNamespace,
    entityRef: stringifyEntityRef(entity),
    'github.com/project-slug':
      entity.metadata.annotations?.['github.com/project-slug'] ??
      `${entityNamespace}/${entity.metadata.name}`,
  };
};

const buildEntityInfoFromRef = (
  entityRef: string,
): IssueRowInput['entityInfo'] | undefined => {
  if (!entityRef?.includes(':')) {
    return undefined;
  }

  const [kindPart, namespaceAndName] = entityRef.split(':');

  if (!namespaceAndName?.includes('/')) {
    return undefined;
  }

  const [namespacePart, namePart] = namespaceAndName.split('/');
  const entityNamespace = namespacePart || 'default';
  const entityName = namePart || entityNamespace;
  const kind = kindPart || 'component';

  return {
    entityName,
    entityNamespace,
    entityRef: `${kind}:${entityNamespace}/${entityName}`,
    'github.com/project-slug': `${entityNamespace}/${entityName}`,
  };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isYardstickIssuePayload = (
  data: unknown,
): data is YardstickIssuePayload => {
  if (!isPlainObject(data)) {
    return false;
  }

  return (
    typeof data.id === 'string' &&
    typeof data.title === 'string' &&
    typeof data.message === 'string' &&
    typeof data.severity === 'string'
  );
};

const yardstickIssueToIssueRow = (
  issue: YardstickIssuePayload,
): IssueRowInput => {
  const entityInfo = isPlainObject(issue._meta?.entityInfo)
    ? (issue._meta?.entityInfo as IssueRowInput['entityInfo'])
    : undefined;
  const resources = Array.isArray(issue._meta?.resources)
    ? (issue._meta?.resources as IssueRowInput['resources'])
    : undefined;
  const remediationSteps =
    typeof issue._meta?.remediationSteps === 'string'
      ? issue._meta.remediationSteps
      : undefined;
  const issueDetails =
    typeof issue._meta?.issueDetails === 'string'
      ? issue._meta.issueDetails
      : undefined;
  const owningTeam =
    typeof issue._meta?.owningTeam === 'string'
      ? issue._meta.owningTeam
      : undefined;
  const frameworkId =
    typeof issue._meta?.frameworkId === 'string'
      ? issue._meta.frameworkId
      : undefined;
  const accountId =
    typeof issue._meta?.accountId === 'string'
      ? issue._meta.accountId
      : undefined;
  const subtitle =
    typeof issue._meta?.subtitle === 'string'
      ? issue._meta.subtitle
      : undefined;
  const resourceID =
    typeof issue._meta?.resourceID === 'string'
      ? issue._meta.resourceID
      : undefined;
  const resourceName =
    typeof issue._meta?.resourceName === 'string'
      ? issue._meta.resourceName
      : undefined;
  const autoFix = isPlainObject(issue.autoFix)
    ? (issue.autoFix as IssueRowInput['autoFix'])
    : undefined;

  return {
    id: issue.id,
    issueId: issue.id,
    name: issue.name,
    issueType: issue.title,
    measurement: issue.name,
    issueDescription: issue.message,
    issueDetails: issueDetails ?? issue.message,
    remediationSteps,
    severity: issue.severity,
    dueDate: issue.dueDate ?? { text: 'N/A', date: null },
    updatedAt: issue.lastUpdated,
    owningTeam,
    entityInfo,
    resources,
    frameworkId,
    accountId,
    subtitle: subtitle ?? issue.title,
    issueName: issue.title,
    resourceID,
    resourceName,
    autoFix,
    _meta: {
      ...issue._meta,
    },
  };
};

export const YardstickIssuePreviewer = (): ReactElement => {
  const [rawJson, setRawJson] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [issueProps, setIssueProps] = useState<IssueActionPreview | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEntityRef, setSelectedEntityRef] = useState<string>(
    resolveDefaultEntityRef,
  );
  const catalogApi = useApi(catalogApiRef);
  const {
    value: catalogEntities,
    loading: catalogEntitiesLoading,
    error: catalogEntitiesError,
  } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: [{ kind: 'Component' }],
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.annotations',
        'spec.owner',
      ],
    });

    return items;
  }, [catalogApi]);

  const entityOptions = useMemo(
    () =>
      (catalogEntities ?? [])
        .map(entity => ({
          ref: stringifyEntityRef(entity),
          label: `${entity.metadata.namespace ?? 'default'}/${
            entity.metadata.name
          }`,
          entity,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catalogEntities],
  );

  const selectedEntity = useMemo(
    () =>
      entityOptions.find(option => option.ref === selectedEntityRef)?.entity,
    [entityOptions, selectedEntityRef],
  );

  const selectedEntityInfo = useMemo(
    () =>
      selectedEntity
        ? buildEntityInfoFromEntity(selectedEntity)
        : buildEntityInfoFromRef(selectedEntityRef),
    [selectedEntity, selectedEntityRef],
  );

  const resetPreviewState = (): void => {
    setIssueProps(null);
    setModalOpen(false);
    setError(null);
  };

  const handlePreview = useCallback(() => {
    try {
      const parsed = JSON.parse(rawJson);
      if (!isYardstickIssuePayload(parsed)) {
        throw new Error('Paste a single Yardstick issue JSON object.');
      }

      const baseIssueRow = yardstickIssueToIssueRow(parsed);
      const issueRow = {
        ...baseIssueRow,
        entityInfo: selectedEntityInfo ?? baseIssueRow.entityInfo,
        owningTeam:
          (selectedEntity?.spec?.owner as string | undefined) ??
          baseIssueRow.owningTeam,
      };

      if (!issueRow.entityInfo) {
        throw new Error(
          'Select a catalog entity so we can populate entity details.',
        );
      }

      const props = buildInsightsIssueActionProps(issueRow);

      setIssueProps(props);
      setModalOpen(true);
      setError(null);
    } catch (err) {
      setIssueProps(null);
      setModalOpen(false);
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to parse Yardstick issue JSON.',
      );
    }
  }, [rawJson, selectedEntityInfo, selectedEntity?.spec?.owner]);

  const handleClear = (): void => {
    setRawJson('');
    resetPreviewState();
  };

  const handlePaste = (value: string): void => {
    setRawJson(value);
    resetPreviewState();
  };

  const handleLoadSample = (): void => {
    setRawJson(sampleIssueJson);
    resetPreviewState();
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={7}>
        <Typography variant="h6" gutterBottom>
          Paste Yardstick issue JSON
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Capture the JSON for a single issue (for example from the Insights API
          response or the browser network tab) and paste it below. The JSON
          follows the Yardstick issue format documented in the{' '}
          <a
            href="https://portal.developer.principal.com/docs/principalinformationservices-emu/component/principal-developer-portal/yardstick/yardstick-contribution/#producing-issues"
            target="_blank"
            rel="noopener noreferrer"
          >
            Yardstick contribution guide (Producing issues)
          </a>
          , is never sent to a backend, and only lives in your browser.
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Catalog entity
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          The selected entity fills the missing entityInfo fields before the
          modal renders.
        </Typography>
        <TextField
          select
          label="Catalog entity"
          fullWidth
          variant="outlined"
          value={selectedEntityRef}
          onChange={event => {
            setSelectedEntityRef(event.target.value);
            setError(null);
          }}
          helperText="Pick the catalog entity to simulate an issue for."
          margin="normal"
          disabled={catalogEntitiesLoading}
        >
          {entityOptions.map(option => (
            <MenuItem key={option.ref} value={option.ref}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {catalogEntitiesLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CircularProgress size={18} />
            <Typography variant="caption" color="textSecondary">
              Loading catalog entities...
            </Typography>
          </div>
        )}
        {catalogEntitiesError && (
          <Alert severity="warning" style={{ marginTop: '8px' }}>
            Unable to load catalog entities. The default entity reference will
            be used.
          </Alert>
        )}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <PasteFromClipboardButton setInput={handlePaste} />
          <Button variant="outlined" size="small" onClick={handleLoadSample}>
            Load sample issue
          </Button>
          <ClearValueButton setValue={handleClear} />
        </div>
        <TextField
          label="Yardstick issue payload"
          multiline
          minRows={16}
          maxRows={24}
          fullWidth
          variant="outlined"
          value={rawJson}
          onChange={event => {
            setRawJson(event.target.value);
            setError(null);
          }}
          placeholder='{"id":"invalid-owner","title":"Invalid spec owner","message":"Add a valid spec owner","severity":"high","dueDate":{"text":"2/25/2025","date":"2025-02-25T00:00:00.000Z"}}'
          inputProps={{
            style: { resize: 'vertical', fontFamily: 'monospace' },
          }}
        />
        {error && (
          <Alert severity="error" style={{ marginTop: '16px' }}>
            {error}
          </Alert>
        )}
        <Button
          variant="contained"
          color="primary"
          style={{ marginTop: '16px' }}
          onClick={handlePreview}
          disabled={!rawJson.trim()}
        >
          Preview Insights modal
        </Button>
      </Grid>
      <Grid item xs={12} lg={5}>
        <Paper style={{ padding: '16px' }}>
          <Typography variant="subtitle1" gutterBottom>
            How it works
          </Typography>
          <Typography variant="body2">
            Paste a single Yardstick issue JSON object. The tool converts that
            issue into the Insights modal using the catalog entity you selected,
            so you can validate issue details quickly and easily.
          </Typography>
        </Paper>
      </Grid>
      {issueProps && (
        <InsightsIssueAction
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          appbar={issueProps.appbar}
          modalContent={issueProps.modalContent}
          supportCard={issueProps.supportCard}
          exceptionCard={issueProps.exceptionCard}
        />
      )}
    </Grid>
  );
};
